import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';

/**
 * Input type components mapping
 * Extensible: add new types here (e.g., 'number', 'autocomplete', 'multiselect')
 */
const InputComponents = {
  text: ({ attribute, value, onChange }) => (
    <TextField
      fullWidth
      label={attribute.label}
      name={attribute.name}
      variant="outlined"
      size="small"
      value={value}
      onChange={(e) => onChange(attribute.name, e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onChange(attribute.name, '')}
              edge="end"
              aria-label="clear"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.default',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
          },
        },
      }}
    />
  ),

  list: ({ attribute, value, onChange }) => (
    <FormControl fullWidth variant="outlined" size="small">
      <InputLabel>{attribute.label}</InputLabel>
      <Select
        label={attribute.label}
        name={attribute.name}
        value={value}
        onChange={(e) => onChange(attribute.name, e.target.value)}
        sx={{
          backgroundColor: 'background.default',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
          },
        }}
      >
        <MenuItem value="">
          <em>All</em>
        </MenuItem>
        {attribute.options?.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ),

  date: ({ attribute, value, onChange }) => (
    <TextField
      fullWidth
      type="date"
      label={attribute.label}
      name={attribute.name}
      variant="outlined"
      size="small"
      value={value}
      onChange={(e) => onChange(attribute.name, e.target.value)}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onChange(attribute.name, '')}
              edge="end"
              aria-label="clear"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.default',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
          },
        },
      }}
    />
  ),

  // Future extensibility: add more types here
  // number: ({ attribute, value, onChange }) => (...),
  // autocomplete: ({ attribute, value, onChange }) => (...),
  // multiselect: ({ attribute, value, onChange }) => (...),
};

/**
 * Sanitize and combine filter values
 * - Trims string values
 * - Removes null, undefined, and empty strings
 * @param {Object} filters - Raw filter values
 * @returns {Object} - Cleaned filter object
 */
const sanitizeFilters = (filters) => {
  const sanitized = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    if (trimmedValue !== '') {
      sanitized[key] = trimmedValue;
    }
  });

  return sanitized;
};

/**
 * Custom hook for debouncing a value
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {any} - Debounced value
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * SearchBox Component
 * 
 * A flexible, configurable search component that renders various input types
 * and communicates filter changes via callback.
 * 
 * @param {Array} attributes - Array of search attribute configurations
 *   Each attribute should have:
 *   - name: string (required) - The parameter name for the filter
 *   - label: string (required) - Display label for the input
 *   - type: 'text' | 'list' | 'date' (default: 'text') - Input type
 *   - options: Array<{label, value}> (required for 'list' type) - Select options
 * 
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 400)
 * @param {Function} onFilterChange - Callback when filters change (after debounce)
 * @param {Object} initialFilters - Initial filter values (optional)
 * 
 * @example
 * <SearchBox
 *   attributes={[
 *     { name: 'search', label: 'Search', type: 'text' },
 *     { name: 'status', label: 'Status', type: 'list', options: [
 *       { label: 'Active', value: 'active' },
 *       { label: 'Inactive', value: 'inactive' }
 *     ]},
 *     { name: 'createdDate', label: 'Created Date', type: 'date' }
 *   ]}
 *   onFilterChange={(filters) => console.log(filters)}
 * />
 */
const SearchBox = ({
  attributes = [],
  debounceMs = 400,
  onFilterChange = null,
  initialFilters = {},
}) => {
  const [localFilters, setLocalFilters] = useState(initialFilters);
  
  // Track if this is the initial mount to avoid triggering onFilterChange
  const isInitialMount = useRef(true);

  // Debounced filters
  const debouncedFilters = useDebounce(localFilters, debounceMs);

  // Handle input change
  const handleChange = useCallback((name, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Call onFilterChange when debounced filters change
  useEffect(() => {
    // Skip the initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const sanitized = sanitizeFilters(debouncedFilters);

    // Log combined search input for debugging
    console.debug('SearchBox - Combined filters:', sanitized);

    // Call callback with sanitized filters
    if (onFilterChange) {
      onFilterChange(sanitized);
    }
  }, [debouncedFilters, onFilterChange]);

  // Clear all filters
  const handleClearAll = useCallback(() => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    
    // Immediately notify parent of cleared filters
    if (onFilterChange) {
      onFilterChange({});
    }
  }, [onFilterChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const sanitized = sanitizeFilters(localFilters);
    return Object.keys(sanitized).length > 0;
  }, [localFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    const sanitized = sanitizeFilters(localFilters);
    return Object.keys(sanitized).length;
  }, [localFilters]);

  // Don't render if no attributes
  if (!attributes || attributes.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'grey.50',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: 0.3,
            }}
          >
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '12px',
                px: 1,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 20,
                textAlign: 'center',
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </Box>

        {hasActiveFilters && (
          <Button
            onClick={handleClearAll}
            size="small"
            color="inherit"
            startIcon={<ClearIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'error.lighter',
              },
            }}
          >
            Clear all
          </Button>
        )}
      </Box>

      {/* Filter Inputs */}
      <Grid container spacing={2}>
        {attributes.map((attr) => {
          const type = attr.type || 'text';
          const InputComponent = InputComponents[type];

          if (!InputComponent) {
            console.warn(`SearchBox: Unknown input type "${type}" for attribute "${attr.name}"`);
            return null;
          }

          // Responsive grid sizing
          // text: larger on desktop, full on mobile
          // list/date: medium on desktop, full on mobile
          const getGridSize = () => {
            switch (type) {
              case 'text':
                return { xs: 12, sm: 6, md: 4, lg: 3 };
              case 'list':
                return { xs: 12, sm: 6, md: 3, lg: 2 };
              case 'date':
                return { xs: 12, sm: 6, md: 3, lg: 2 };
              default:
                return { xs: 12, sm: 6, md: 4, lg: 3 };
            }
          };

          const gridSize = getGridSize();

          return (
            <Grid item key={attr.name} {...gridSize}>
              <InputComponent
                attribute={attr}
                value={localFilters[attr.name] || ''}
                onChange={handleChange}
              />
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default SearchBox;
